import React from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import _ from 'lodash';
import parseAndGetBigNumber from '../utils/parseAndGetBigNumber';
import BigNumber from 'bignumber.js';
import {UnconnectedButton} from '../components/Button';
import Theme from '../themes';
import replaceNewLinesForHtml from '../utils/replaceNewLines';
import DOMPurify from 'dompurify';
import moment from 'moment';

BigNumber.config({
  FORMAT: {
    decimalSeparator: ',',
    groupSeparator: '.',
    groupSize: 3
  }
});

const mapping = [
  {
    match: '{{firstName}}',
    resolve: ctx => DOMPurify.sanitize(_.get(ctx, ['firstName'], ''))
  },
  {
    match: '{{amount}}',
    resolve: ctx => {
      const amountHasValue = ctx.amount_m !== '' && !_.isNil(ctx.amount_m);
      const val = amountHasValue ? ctx.amount_m : ctx.customAmount_m;

      return toBigNumber(val);
    }
  },
  {
    match: '{{amountLabel}}',
    resolve: ctx => DOMPurify.sanitize(_.get(ctx, ['amountLabel'], ''))
  },
  {
    match: '{{frequency}}',
    resolve: ctx => translateFrequency(ctx.frequency)
  },
  {
    match: '{{frequencyFormatted}}',
    resolve: ctx => {
      return ctx.frequency === 'oneOff' ? 'eenmalig' : `per ${translateFrequency(ctx.frequency)}`;
    }
  },
  {
    match: '({{label:\\")(.+)(\\")(, url:\\")(.+)(\\")}}',
    resolve: (ctx, text, themeName) => {
      const matches = text.match(/({{label:")(.+)(")(, url:")(.+)(")}}/);

      if (_.isEmpty(matches)) {
        return null;
      }

      const label = matches[2];
      const url = matches[5];

      let button = renderToStaticMarkup(<Theme themeFromState={themeName}><UnconnectedButton label={label} /></Theme>);
      button = _.replace(button, '<button', `<button type="button" onclick="window.open('${url}')"`);
      return button;
    }
  },
  {
    match: '({{text:\\")(.+)(\\")(, url:\\")(.+)(\\")}}',
    // We return a function, so every globally hit match will be processed
    resolve: () => (m) => {
      const matches = m.match(/({{text:")(.+)(")(, url:")(.+)(")}}/);

      if (_.isEmpty(matches)) {
        return null;
      }

      const label = matches[2];
      const url = matches[5];

      return `<a href="${url}">${label}</a>`;
    }
  },
  {
    match: '{{externalVariable}}',
    resolve: ctx => DOMPurify.sanitize(_.get(ctx, ['externalVariable'], ''))
  },
  {
    match: '{{externalReferenceName}}',
    resolve: ctx => DOMPurify.sanitize(_.get(ctx, ['externalReferenceName'], ''))
  },
  {
    match: '{{externalReferenceUrl}}',
    resolve: ctx => DOMPurify.sanitize(_.get(ctx, ['externalReferenceUrl'], ''))
  },
  {
    match: '{{externalReferenceId}}',
    resolve: ctx => DOMPurify.sanitize(_.get(ctx, ['externalReferenceId'], ''))
  },
  {
    match: '{{externalReferenceIdExtra}}',
    resolve: ctx => DOMPurify.sanitize(_.get(ctx, ['externalReferenceIdExtra'], ''))
  },
  {
    match: '{{externalReferenceDescription}}',
    resolve: ctx => DOMPurify.sanitize(_.get(ctx, ['externalReferenceDescription'], ''))
  },
  {
    match: '{{variableFieldAnswer}}',
    resolve: ctx => {
      const answer = ctx.variableFieldAnswer || ctx.variableFieldDefaultAnswer || '';
      return DOMPurify.sanitize(replaceNewLinesForHtml(answer));
    }
  },
  {
    match: '{{variableFieldLabel}}',
    resolve: ctx => ctx.variableFieldLabel || ''
  },
  {
    match: '{{orderDetails}}',
    resolve: ctx => {
      const quantity = _.toInteger(ctx.productAmount);
      const productName = _.get(ctx, ['productName']);
      const productNamePlural = _.get(ctx, ['productNamePlural']);

      if (quantity > 1) {
        return `${quantity} ${productNamePlural}`;
      } else if (quantity === 1) {
        return `${quantity} ${productName}`;
      } else {
        return '-';
      }
    }
  },
  {
    match: '{{productName}}',
    resolve: ctx => ctx.productName || ''
  },
  {
    match: '{{productNamePlural}}',
    resolve: ctx => ctx.productNamePlural || ''
  },
  {
    match: '{{period}}',
    resolve: ctx => ctx.period ? `${ctx.period} maanden` : ''
  },
  {
    match: '{{adminCostContribution}}',
    resolve: ctx => `${toBigNumber(_.get(ctx, 'adminCostContributionAmount_m'), '0')}`
  },
  {
    match: '{{compensationAmount}}',
    resolve: ctx => `${toBigNumber(_.get(ctx, 'compensationAmount_m'), '0')}`
  },
  {
    match: '{{donationAmount}}',
    resolve: ctx => {
      const amountHasValue = ctx.donationAmount_m !== '' && !_.isNil(ctx.donationAmount_m);
      const val = amountHasValue ? ctx.donationAmount_m : ctx.donationCustomAmount_m;
      return toBigNumber(val);
    }
  },
  {
    match: '{{currentYear}}',
    resolve: () => {
      return moment().year();
    }
  }
];
export default function parseText(ctx, text, themeName) {
  if (_.isNil(text)) {
    return '';
  }

  mapping.forEach(item => {
    text = text.replace(new RegExp(item.match, 'g'), item.resolve(ctx, text, themeName));
  });

  return text;
}

function toBigNumber(value) {
  const bigAmount = parseAndGetBigNumber(value);

  if (bigAmount === undefined) {
    return;
  }

  return bigAmount.toFormat(2);
}

function translateFrequency(frequency) {
  switch (frequency) {
    case 'oneOff':
      return 'eenmalig';
    case 'monthly':
      return 'maand';
    case 'quarterly':
      return 'kwartaal';
    case 'halfYearly':
      return 'half jaar';
    case 'yearly':
    default:
      return 'jaar';
  }
}
